/**
 * BulkCreateAssignments Page
 * Create a series of assignments for the same customer
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeft,
  Plus,
  Trash2,
  Calendar,
  Search,
  Loader2,
  ChevronDown,
  ChevronUp,
  Copy,
  X,
  Check
} from 'lucide-react'
import toast from 'react-hot-toast'
import { supabase } from '../../lib/supabase'
import { TimeDropdown } from '../../components/TimeDropdown'
import { CalendarSlidePanel } from '../../components/assignment/CalendarSlidePanel'
import {
  generateDates,
  createBulkAssignments,
  type RecurrenceType,
  type EndMode,
  type ArticleItem,
  type BulkAssignmentRow,
  type BulkCreateProgress
} from '../../services/bulkAssignments'

export function BulkCreateAssignments() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  // =============================================
  // CONFIGURATION STATE
  // =============================================
  const [customerId, setCustomerId] = useState('')
  const [customerSearch, setCustomerSearch] = useState('')
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false)
  const [baseTitle, setBaseTitle] = useState('')
  const [baseDescription, setBaseDescription] = useState('')
  const [startDate, setStartDate] = useState('')
  const [recurrence, setRecurrence] = useState<RecurrenceType>('weekly')
  const [timesPerWeek, setTimesPerWeek] = useState(1)
  const [endMode, setEndMode] = useState<EndMode>('count')
  const [count, setCount] = useState(10)
  const [endDate, setEndDate] = useState('')
  const [defaultSubcontractorIds, setDefaultSubcontractorIds] = useState<string[]>([])
  const [defaultStartTime, setDefaultStartTime] = useState('')
  const [defaultEndTime, setDefaultEndTime] = useState('')
  const [defaultArticles, setDefaultArticles] = useState<ArticleItem[]>([])
  const [articleSearch, setArticleSearch] = useState('')
  const [showArticleSelector, setShowArticleSelector] = useState(false)
  const [showTgDropdown, setShowTgDropdown] = useState(false)

  // =============================================
  // WORKSHEET STATE
  // =============================================
  const [rows, setRows] = useState<BulkAssignmentRow[]>([])
  const [expandedArticleRows, setExpandedArticleRows] = useState<Set<string>>(new Set())
  const [rowArticleSearch, setRowArticleSearch] = useState<Record<string, string>>({})
  const [showRowArticleSelector, setShowRowArticleSelector] = useState<Record<string, boolean>>({})
  const [showRowTgDropdown, setShowRowTgDropdown] = useState<Record<string, boolean>>({})

  // =============================================
  // CREATION STATE
  // =============================================
  const [creating, setCreating] = useState(false)
  const [progress, setProgress] = useState<BulkCreateProgress | null>(null)
  const [showConfirm, setShowConfirm] = useState(false)

  // =============================================
  // CALENDAR PANEL STATE
  // =============================================
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [calendarRowId, setCalendarRowId] = useState<string | null>(null)

  // =============================================
  // DATA QUERIES
  // =============================================
  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('*')
        .eq('active', true)
        .order('full_name')
      if (error) throw error
      return data
    }
  })

  const { data: subcontractors = [] } = useQuery({
    queryKey: ['subcontractors'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subcontractors')
        .select('*')
        .eq('is_active', true)
        .order('name')
      if (error) throw error
      return data
    }
  })

  const { data: articles = [] } = useQuery({
    queryKey: ['articles'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('*')
        .order('name')
      if (error) throw error
      return data
    }
  })

  // Customer price list
  const selectedCustomer = customers.find(c => c.id === customerId)
  const customerAddress = selectedCustomer
    ? `${selectedCustomer.address_street || ''}, ${selectedCustomer.address_city || ''}`.replace(/^, |, $/g, '')
    : ''

  const { data: customerPrices = {} } = useQuery({
    queryKey: ['customer-prices', selectedCustomer?.price_list_id],
    queryFn: async () => {
      if (!selectedCustomer?.price_list_id) return {}
      const { data, error } = await supabase
        .from('price_list_articles')
        .select('article_id, price_excl_vat')
        .eq('price_list_id', selectedCustomer.price_list_id)
      if (error) throw error
      const priceMap: Record<string, number> = {}
      data?.forEach(p => {
        priceMap[p.article_id] = p.price_excl_vat
      })
      return priceMap
    },
    enabled: !!selectedCustomer?.price_list_id
  })

  // =============================================
  // COMPUTED
  // =============================================
  const activeRows = useMemo(() => rows.filter(r => !r.removed), [rows])
  const calendarRow = calendarRowId ? rows.find(r => r.id === calendarRowId) : null

  const filteredCustomers = useMemo(() => {
    if (!customerSearch) return customers.slice(0, 20)
    const lower = customerSearch.toLowerCase()
    return customers.filter(c =>
      c.full_name?.toLowerCase().includes(lower) ||
      c.company_name?.toLowerCase().includes(lower) ||
      c.address_street?.toLowerCase().includes(lower)
    ).slice(0, 20)
  }, [customers, customerSearch])

  // =============================================
  // HANDLERS: CONFIGURATION
  // =============================================
  const selectCustomer = (customer: typeof customers[0]) => {
    setCustomerId(customer.id)
    setCustomerSearch(customer.full_name || customer.company_name || '')
    setShowCustomerDropdown(false)
  }

  const addDefaultArticle = (articleId: string) => {
    const price = customerPrices[articleId]
    setDefaultArticles(prev => [...prev, {
      articleId,
      quantity: 1,
      customPrice: price
    }])
    setArticleSearch('')
    setShowArticleSelector(false)
  }

  const removeDefaultArticle = (articleId: string) => {
    setDefaultArticles(prev => prev.filter(a => a.articleId !== articleId))
  }

  const updateDefaultArticleQuantity = (articleId: string, quantity: number) => {
    setDefaultArticles(prev => prev.map(a =>
      a.articleId === articleId ? { ...a, quantity: Math.max(1, quantity) } : a
    ))
  }

  // =============================================
  // HANDLERS: GENERATE ROWS
  // =============================================
  const handleGenerate = () => {
    if (!startDate) {
      toast.error('Välj startdatum')
      return
    }
    if (endMode === 'count' && (!count || count < 1)) {
      toast.error('Ange antal tillfällen')
      return
    }
    if (endMode === 'endDate' && !endDate) {
      toast.error('Välj slutdatum')
      return
    }

    const dates = generateDates(startDate, recurrence, endMode, count, endDate, recurrence === 'weekly' ? timesPerWeek : 1)
    if (dates.length === 0) {
      toast.error('Inga datum genererades')
      return
    }

    const newRows: BulkAssignmentRow[] = dates.map((date, index) => ({
      id: crypto.randomUUID(),
      title: baseTitle || `Uppdrag ${index + 1}`,
      description: baseDescription,
      start_date: date,
      start_time: defaultStartTime,
      end_time: defaultEndTime,
      subcontractor_ids: [...defaultSubcontractorIds],
      articles: defaultArticles.map(a => ({ ...a })),
      removed: false
    }))

    setRows(newRows)
    toast.success(`${newRows.length} rader genererade`)
  }

  // =============================================
  // HANDLERS: WORKSHEET
  // =============================================
  const updateRow = (rowId: string, field: keyof BulkAssignmentRow, value: unknown) => {
    setRows(prev => prev.map(r =>
      r.id === rowId ? { ...r, [field]: value } : r
    ))
  }

  const removeRow = (rowId: string) => {
    setRows(prev => prev.map(r =>
      r.id === rowId ? { ...r, removed: true } : r
    ))
  }

  const applyToAll = (field: 'subcontractor_ids' | 'start_time' | 'end_time', value: unknown) => {
    setRows(prev => prev.map(r =>
      r.removed ? r : { ...r, [field]: field === 'subcontractor_ids' ? [...(value as string[])] : value }
    ))
    toast.success('Tillämpat på alla rader')
  }

  const toggleArticleRow = (rowId: string) => {
    setExpandedArticleRows(prev => {
      const next = new Set(prev)
      if (next.has(rowId)) next.delete(rowId)
      else next.add(rowId)
      return next
    })
  }

  const addArticleToRow = (rowId: string, articleId: string) => {
    const price = customerPrices[articleId]
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r
      if (r.articles.some(a => a.articleId === articleId)) return r
      return { ...r, articles: [...r.articles, { articleId, quantity: 1, customPrice: price }] }
    }))
    setRowArticleSearch(prev => ({ ...prev, [rowId]: '' }))
    setShowRowArticleSelector(prev => ({ ...prev, [rowId]: false }))
  }

  const removeArticleFromRow = (rowId: string, articleId: string) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r
      return { ...r, articles: r.articles.filter(a => a.articleId !== articleId) }
    }))
  }

  const updateRowArticleQuantity = (rowId: string, articleId: string, quantity: number) => {
    setRows(prev => prev.map(r => {
      if (r.id !== rowId) return r
      return {
        ...r,
        articles: r.articles.map(a =>
          a.articleId === articleId ? { ...a, quantity: Math.max(1, quantity) } : a
        )
      }
    }))
  }

  // =============================================
  // HANDLERS: CREATE
  // =============================================
  const handleCreate = async () => {
    setShowConfirm(false)
    setCreating(true)
    setProgress(null)

    try {
      const result = await createBulkAssignments(
        rows,
        customerId,
        customerAddress,
        (p) => setProgress(p)
      )

      if (result.failed === 0) {
        toast.success(`${result.success} uppdrag skapade!`)
        queryClient.invalidateQueries({ queryKey: ['assignments'] })
        navigate(`/admin/assignments?customer=${customerId}`)
      } else {
        toast.error(`${result.success} lyckades, ${result.failed} misslyckades`)
        result.errors.forEach(err => console.error(err))
      }
    } catch (err) {
      toast.error('Fel vid skapande av uppdrag')
      console.error(err)
    } finally {
      setCreating(false)
    }
  }

  const canCreate = customerId && activeRows.length > 0 &&
    activeRows.every(r => r.title && r.start_date)

  // =============================================
  // RENDER
  // =============================================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/admin/assignments')}
          className="p-2 rounded-lg hover:bg-nature-100 dark:hover:bg-nature-700"
        >
          <ArrowLeft className="h-5 w-5 text-nature-600 dark:text-nature-300" />
        </button>
        <h1 className="text-xl sm:text-2xl font-bold text-nature-900 dark:text-nature-100">
          Skapa serieuppdrag
        </h1>
      </div>

      {/* ============================================= */}
      {/* CONFIGURATION SECTION */}
      {/* ============================================= */}
      <div className="bg-white dark:bg-nature-800 rounded-lg shadow-sm border border-nature-200 dark:border-nature-700 p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-4">Konfiguration</h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* LEFT COLUMN: Kund, titel, beskrivning */}
          <div className="space-y-4">
            {/* Customer */}
            <div>
              <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                Kund *
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
                <input
                  type="text"
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value)
                    setShowCustomerDropdown(true)
                    if (!e.target.value) setCustomerId('')
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                  placeholder="Sök kund..."
                  className="input-field pl-9 w-full"
                />
                {showCustomerDropdown && filteredCustomers.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 max-h-48 overflow-y-auto">
                    {filteredCustomers.map(customer => (
                      <button
                        key={customer.id}
                        type="button"
                        onClick={() => selectCustomer(customer)}
                        className="w-full px-4 py-2 text-left hover:bg-nature-100 dark:hover:bg-nature-700"
                      >
                        <span className="text-nature-900 dark:text-nature-100">
                          {customer.full_name}
                        </span>
                        {customer.company_name && (
                          <span className="text-nature-500 text-sm ml-2">({customer.company_name})</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {customerAddress && (
                <p className="mt-1 text-sm text-nature-500">Plats: {customerAddress}</p>
              )}
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                Bastitel *
              </label>
              <input
                type="text"
                value={baseTitle}
                onChange={(e) => setBaseTitle(e.target.value)}
                placeholder="T.ex. Gräsklippning hos Andersson"
                className="input-field w-full"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                Basbeskrivning
              </label>
              <textarea
                value={baseDescription}
                onChange={(e) => setBaseDescription(e.target.value)}
                placeholder="Beskriv uppdraget..."
                className="input-field w-full"
                rows={4}
              />
            </div>
          </div>

          {/* RIGHT COLUMN: Schema, TG/tider, artiklar */}
          <div className="space-y-4">
            {/* Date + Recurrence */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Startdatum *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Upprepning
                </label>
                <select
                  value={recurrence}
                  onChange={(e) => setRecurrence(e.target.value as RecurrenceType)}
                  className="input-field w-full"
                >
                  <option value="weekly">Varje vecka</option>
                  <option value="biweekly">Varannan vecka</option>
                  <option value="triweekly">Var tredje vecka</option>
                  <option value="monthly">Varje månad</option>
                </select>
              </div>
              {recurrence === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                    Gånger/vecka
                  </label>
                  <select
                    value={timesPerWeek}
                    onChange={(e) => setTimesPerWeek(parseInt(e.target.value))}
                    className="input-field w-full"
                  >
                    <option value={1}>1 gång</option>
                    <option value={2}>2 gånger</option>
                    <option value={3}>3 gånger</option>
                    <option value={4}>4 gånger</option>
                    <option value={5}>5 gånger</option>
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Slut
                </label>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={endMode === 'count'}
                      onChange={() => setEndMode('count')}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-300">Antal:</span>
                    <input
                      type="number"
                      min={1}
                      max={52}
                      value={count}
                      onChange={(e) => setCount(parseInt(e.target.value) || 1)}
                      disabled={endMode !== 'count'}
                      className="input-field w-20"
                    />
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      checked={endMode === 'endDate'}
                      onChange={() => setEndMode('endDate')}
                      className="text-primary-600"
                    />
                    <span className="text-sm text-nature-700 dark:text-nature-300">Slutdatum:</span>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      disabled={endMode !== 'endDate'}
                      className="input-field flex-1"
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* TG + Times */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Standard-TG
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowTgDropdown(!showTgDropdown)}
                    className="input-field w-full text-left flex items-center justify-between min-h-[38px]"
                  >
                    {defaultSubcontractorIds.length === 0 ? (
                      <span className="text-nature-400">Välj TG...</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {defaultSubcontractorIds.map(id => {
                          const sub = subcontractors.find(s => s.id === id)
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300"
                            >
                              {sub?.name}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setDefaultSubcontractorIds(prev => prev.filter(i => i !== id))
                                }}
                                className="ml-0.5 hover:text-primary-900 dark:hover:text-primary-100"
                              >
                                <X className="h-3 w-3" />
                              </button>
                            </span>
                          )
                        })}
                      </div>
                    )}
                    <ChevronDown className="h-4 w-4 text-nature-400 shrink-0 ml-1" />
                  </button>
                  {showTgDropdown && (
                    <div className="absolute z-50 mt-1 w-full bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 max-h-48 overflow-y-auto">
                      {subcontractors.map(s => {
                        const isSelected = defaultSubcontractorIds.includes(s.id)
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setDefaultSubcontractorIds(prev => prev.filter(i => i !== s.id))
                              } else {
                                setDefaultSubcontractorIds(prev => [...prev, s.id])
                              }
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-nature-100 dark:hover:bg-nature-700 flex items-center gap-2"
                          >
                            <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                              isSelected
                                ? 'bg-primary-600 border-primary-600 text-white'
                                : 'border-nature-300 dark:border-nature-600'
                            }`}>
                              {isSelected && <Check className="h-3 w-3" />}
                            </div>
                            <span className="text-nature-900 dark:text-nature-100 text-sm">{s.name}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Starttid
                </label>
                <TimeDropdown
                  value={defaultStartTime}
                  onChange={setDefaultStartTime}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                  Sluttid
                </label>
                <TimeDropdown
                  value={defaultEndTime}
                  onChange={setDefaultEndTime}
                  className="w-full"
                />
              </div>
            </div>

            {/* Default Articles */}
            <div>
              <label className="block text-sm font-medium text-nature-700 dark:text-nature-300 mb-1">
                Standardartiklar
              </label>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-nature-400" />
                <input
                  type="text"
                  value={articleSearch}
                  onChange={(e) => {
                    setArticleSearch(e.target.value)
                    setShowArticleSelector(true)
                  }}
                  onFocus={() => setShowArticleSelector(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setShowArticleSelector(false)
                      setArticleSearch('')
                    }
                  }}
                  placeholder="Sök och lägg till artikel..."
                  className="input-field pl-9 w-full"
                />
                {showArticleSelector && (
                  <div className="absolute z-50 mt-1 w-full bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 max-h-48 overflow-y-auto">
                    {articles
                      .filter(a => !defaultArticles.some(d => d.articleId === a.id))
                      .filter(a => a.name.toLowerCase().includes(articleSearch.toLowerCase()))
                      .map(article => (
                        <button
                          key={article.id}
                          type="button"
                          onClick={() => addDefaultArticle(article.id)}
                          className="w-full px-4 py-2 text-left hover:bg-nature-100 dark:hover:bg-nature-700 flex items-center justify-between"
                        >
                          <span className="text-nature-900 dark:text-nature-100">{article.name}</span>
                        </button>
                      ))}
                    {articles
                      .filter(a => !defaultArticles.some(d => d.articleId === a.id))
                      .filter(a => a.name.toLowerCase().includes(articleSearch.toLowerCase()))
                      .length === 0 && (
                      <p className="px-4 py-2 text-nature-500 text-sm">
                        {articleSearch ? 'Ingen matchning' : 'Alla artiklar är redan tillagda'}
                      </p>
                    )}
                  </div>
                )}
              </div>
              {defaultArticles.length > 0 && (
                <div className="space-y-1">
                  {defaultArticles.map(item => {
                    const article = articles.find(a => a.id === item.articleId)
                    return (
                      <div key={item.articleId} className="flex items-center gap-2 bg-nature-50 dark:bg-nature-700 rounded px-3 py-1.5">
                        <span className="flex-1 text-sm text-nature-900 dark:text-nature-100">{article?.name}</span>
                        <input
                          type="number"
                          min={1}
                          value={item.quantity}
                          onChange={(e) => updateDefaultArticleQuantity(item.articleId, parseInt(e.target.value) || 1)}
                          className="input-field w-16 text-sm py-0.5"
                        />
                        <span className="text-xs text-nature-500">{article?.unit || 'st'}</span>
                        <button onClick={() => removeDefaultArticle(item.articleId)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <div className="pt-4">
          <button
            onClick={handleGenerate}
            disabled={!startDate}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Generera
          </button>
        </div>
      </div>

      {/* ============================================= */}
      {/* WORKSHEET SECTION */}
      {/* ============================================= */}
      {rows.length > 0 && (
        <div className="bg-white dark:bg-nature-800 rounded-lg shadow-sm border border-nature-200 dark:border-nature-700 p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100">
              Arbetsblad — {activeRows.length} uppdrag
            </h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => applyToAll('subcontractor_ids', defaultSubcontractorIds)}
                className="text-xs px-2 py-1 bg-nature-100 dark:bg-nature-700 rounded hover:bg-nature-200 dark:hover:bg-nature-600 text-nature-700 dark:text-nature-300"
                title="Tillämpa TG på alla"
              >
                <Copy className="h-3 w-3 inline mr-1" />
                TG → alla
              </button>
              <button
                onClick={() => applyToAll('start_time', defaultStartTime)}
                className="text-xs px-2 py-1 bg-nature-100 dark:bg-nature-700 rounded hover:bg-nature-200 dark:hover:bg-nature-600 text-nature-700 dark:text-nature-300"
                title="Tillämpa starttid på alla"
              >
                <Copy className="h-3 w-3 inline mr-1" />
                Starttid → alla
              </button>
              <button
                onClick={() => applyToAll('end_time', defaultEndTime)}
                className="text-xs px-2 py-1 bg-nature-100 dark:bg-nature-700 rounded hover:bg-nature-200 dark:hover:bg-nature-600 text-nature-700 dark:text-nature-300"
                title="Tillämpa sluttid på alla"
              >
                <Copy className="h-3 w-3 inline mr-1" />
                Sluttid → alla
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-nature-200 dark:border-nature-700">
                  <th className="text-left py-2 px-2 text-nature-600 dark:text-nature-400 font-medium w-8">#</th>
                  <th className="text-left py-2 px-2 text-nature-600 dark:text-nature-400 font-medium">Datum</th>
                  <th className="text-left py-2 px-2 text-nature-600 dark:text-nature-400 font-medium">Start</th>
                  <th className="text-left py-2 px-2 text-nature-600 dark:text-nature-400 font-medium">Slut</th>
                  <th className="text-left py-2 px-2 text-nature-600 dark:text-nature-400 font-medium">Titel</th>
                  <th className="text-left py-2 px-2 text-nature-600 dark:text-nature-400 font-medium">TG</th>
                  <th className="text-left py-2 px-2 text-nature-600 dark:text-nature-400 font-medium">Detaljer</th>
                  <th className="text-center py-2 px-2 text-nature-600 dark:text-nature-400 font-medium w-10"></th>
                  <th className="text-center py-2 px-2 text-nature-600 dark:text-nature-400 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  if (row.removed) return null
                  const isExpanded = expandedArticleRows.has(row.id)
                  const rowNum = rows.filter((r, i) => !r.removed && i <= index).length
                  return (
                    <RowGroup
                      key={row.id}
                      row={row}
                      rowNum={rowNum}
                      isExpanded={isExpanded}
                      subcontractors={subcontractors}
                      articles={articles}
                      customerPrices={customerPrices}
                      rowArticleSearch={rowArticleSearch[row.id] || ''}
                      showRowArticleSelector={showRowArticleSelector[row.id] || false}
                      showTgDropdown={showRowTgDropdown[row.id] || false}
                      onUpdate={updateRow}
                      onRemove={removeRow}
                      onToggleArticles={toggleArticleRow}
                      onAddArticle={addArticleToRow}
                      onRemoveArticle={removeArticleFromRow}
                      onUpdateArticleQuantity={updateRowArticleQuantity}
                      onSetRowArticleSearch={(val) => setRowArticleSearch(prev => ({ ...prev, [row.id]: val }))}
                      onSetShowRowArticleSelector={(val) => setShowRowArticleSelector(prev => ({ ...prev, [row.id]: val }))}
                      onSetShowTgDropdown={(val) => setShowRowTgDropdown(prev => ({ ...prev, [row.id]: val }))}
                      onOpenCalendar={() => {
                        setCalendarRowId(row.id)
                        setCalendarOpen(true)
                      }}
                    />
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Create Button */}
          <div className="mt-6 flex items-center gap-4">
            <button
              onClick={() => setShowConfirm(true)}
              disabled={!canCreate || creating}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {creating ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Skapa {activeRows.length} uppdrag
            </button>

            {/* Progress */}
            {progress && (
              <div className="flex-1 max-w-xs">
                <div className="flex items-center justify-between text-xs text-nature-600 dark:text-nature-400 mb-1">
                  <span>{progress.current}/{progress.total}</span>
                  <span>{progress.success} lyckade{progress.failed > 0 ? `, ${progress.failed} misslyckade` : ''}</span>
                </div>
                <div className="w-full bg-nature-200 dark:bg-nature-700 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* CONFIRM DIALOG */}
      {/* ============================================= */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-nature-800 rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold text-nature-900 dark:text-nature-100 mb-2">
              Bekräfta skapande
            </h3>
            <p className="text-nature-600 dark:text-nature-400 mb-4">
              Skapa {activeRows.length} uppdrag för{' '}
              <span className="font-medium text-nature-900 dark:text-nature-100">
                {selectedCustomer?.full_name || selectedCustomer?.company_name}
              </span>
              ?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm text-nature-700 dark:text-nature-300 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg"
              >
                Avbryt
              </button>
              <button
                onClick={handleCreate}
                className="btn-primary"
              >
                Skapa
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================= */}
      {/* CALENDAR SLIDE PANEL */}
      {/* ============================================= */}
      {calendarRow && (
        <CalendarSlidePanel
          isOpen={calendarOpen}
          onClose={() => {
            setCalendarOpen(false)
            setCalendarRowId(null)
          }}
          assignmentId=""
          assignmentStatus="ohanterad"
          currentDate={calendarRow.start_date || null}
          onDateChange={(date) => {
            if (calendarRowId) {
              updateRow(calendarRowId, 'start_date', date)
            }
          }}
          assignedSubcontractorIds={calendarRow.subcontractor_ids}
          onAddSubcontractor={(sub) => {
            if (calendarRowId) {
              const row = rows.find(r => r.id === calendarRowId)
              if (row && !row.subcontractor_ids.includes(sub.id)) {
                updateRow(calendarRowId, 'subcontractor_ids', [...row.subcontractor_ids, sub.id])
              }
            }
          }}
          assignmentTitle={calendarRow.title}
        />
      )}
    </div>
  )
}

// =============================================
// ROW GROUP COMPONENT
// =============================================
interface RowGroupProps {
  row: BulkAssignmentRow
  rowNum: number
  isExpanded: boolean
  subcontractors: Array<{ id: string; name: string; color?: string }>
  articles: Array<{ id: string; name: string; unit?: string }>
  customerPrices: Record<string, number>
  rowArticleSearch: string
  showRowArticleSelector: boolean
  showTgDropdown: boolean
  onUpdate: (rowId: string, field: keyof BulkAssignmentRow, value: unknown) => void
  onRemove: (rowId: string) => void
  onToggleArticles: (rowId: string) => void
  onAddArticle: (rowId: string, articleId: string) => void
  onRemoveArticle: (rowId: string, articleId: string) => void
  onUpdateArticleQuantity: (rowId: string, articleId: string, quantity: number) => void
  onSetRowArticleSearch: (val: string) => void
  onSetShowRowArticleSelector: (val: boolean) => void
  onSetShowTgDropdown: (val: boolean) => void
  onOpenCalendar: () => void
}

function RowGroup({
  row,
  rowNum,
  isExpanded,
  subcontractors,
  articles,
  customerPrices,
  rowArticleSearch,
  showRowArticleSelector,
  showTgDropdown,
  onUpdate,
  onRemove,
  onToggleArticles,
  onAddArticle,
  onRemoveArticle,
  onUpdateArticleQuantity,
  onSetRowArticleSearch,
  onSetShowRowArticleSelector,
  onSetShowTgDropdown,
  onOpenCalendar
}: RowGroupProps) {
  return (
    <>
      <tr className="border-b border-nature-100 dark:border-nature-700/50 hover:bg-nature-50 dark:hover:bg-nature-700/30">
        {/* # */}
        <td className="py-2 px-2 text-nature-500 dark:text-nature-400">{rowNum}</td>

        {/* Datum */}
        <td className="py-2 px-2">
          <input
            type="date"
            value={row.start_date}
            onChange={(e) => onUpdate(row.id, 'start_date', e.target.value)}
            className="input-field py-1 text-sm w-36"
          />
        </td>

        {/* Start */}
        <td className="py-2 px-2">
          <TimeDropdown
            value={row.start_time}
            onChange={(val) => onUpdate(row.id, 'start_time', val)}
            className="py-1 text-sm w-24"
          />
        </td>

        {/* Slut */}
        <td className="py-2 px-2">
          <TimeDropdown
            value={row.end_time}
            onChange={(val) => onUpdate(row.id, 'end_time', val)}
            className="py-1 text-sm w-24"
          />
        </td>

        {/* Titel */}
        <td className="py-2 px-2">
          <input
            type="text"
            value={row.title}
            onChange={(e) => onUpdate(row.id, 'title', e.target.value)}
            className="input-field py-1 text-sm w-full min-w-[140px]"
          />
        </td>

        {/* TG */}
        <td className="py-2 px-2">
          <div className="relative">
            <button
              type="button"
              onClick={() => onSetShowTgDropdown(!showTgDropdown)}
              className="input-field py-1 text-sm w-40 text-left flex items-center justify-between min-h-[30px]"
            >
              {row.subcontractor_ids.length === 0 ? (
                <span className="text-nature-400">—</span>
              ) : (
                <div className="flex flex-wrap gap-0.5">
                  {row.subcontractor_ids.map(id => {
                    const sub = subcontractors.find(s => s.id === id)
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300"
                      >
                        {sub?.name}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            onUpdate(row.id, 'subcontractor_ids', row.subcontractor_ids.filter(i => i !== id))
                          }}
                          className="ml-0.5 hover:text-primary-900 dark:hover:text-primary-100"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    )
                  })}
                </div>
              )}
              <ChevronDown className="h-3 w-3 text-nature-400 shrink-0 ml-0.5" />
            </button>
            {showTgDropdown && (
              <div className="absolute z-50 mt-1 w-48 bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 max-h-40 overflow-y-auto">
                {subcontractors.map(s => {
                  const isSelected = row.subcontractor_ids.includes(s.id)
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => {
                        const newIds = isSelected
                          ? row.subcontractor_ids.filter(i => i !== s.id)
                          : [...row.subcontractor_ids, s.id]
                        onUpdate(row.id, 'subcontractor_ids', newIds)
                      }}
                      className="w-full px-2 py-1.5 text-left hover:bg-nature-100 dark:hover:bg-nature-700 flex items-center gap-2"
                    >
                      <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                        isSelected
                          ? 'bg-primary-600 border-primary-600 text-white'
                          : 'border-nature-300 dark:border-nature-600'
                      }`}>
                        {isSelected && <Check className="h-2.5 w-2.5" />}
                      </div>
                      <span className="text-nature-900 dark:text-nature-100 text-xs">{s.name}</span>
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </td>

        {/* Detaljer (beskrivning + artiklar) */}
        <td className="py-2 px-2">
          <button
            onClick={() => onToggleArticles(row.id)}
            className="flex items-center gap-1 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            {row.articles.length} art.{row.description ? ' + beskr.' : ''}
            {isExpanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
          </button>
        </td>

        {/* Calendar */}
        <td className="py-2 px-2 text-center">
          <button
            onClick={onOpenCalendar}
            className="p-1 rounded hover:bg-nature-100 dark:hover:bg-nature-700 text-nature-500 dark:text-nature-400"
            title="Visa kalender"
          >
            <Calendar className="h-4 w-4" />
          </button>
        </td>

        {/* Remove */}
        <td className="py-2 px-2 text-center">
          <button
            onClick={() => onRemove(row.id)}
            className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500"
            title="Ta bort rad"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </td>
      </tr>

      {/* Expanded details row */}
      {isExpanded && (
        <tr className="bg-nature-50/50 dark:bg-nature-700/20">
          <td colSpan={9} className="px-4 py-3">
            <div className="space-y-3">
              {/* Description */}
              <div>
                <label className="block text-xs font-medium text-nature-600 dark:text-nature-400 mb-1">Beskrivning</label>
                <textarea
                  value={row.description}
                  onChange={(e) => onUpdate(row.id, 'description', e.target.value)}
                  placeholder="Beskrivning för detta uppdrag..."
                  className="input-field w-full text-sm"
                  rows={2}
                />
              </div>

              {/* Article search for this row */}
              <div className="relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-nature-400" />
                <input
                  type="text"
                  value={rowArticleSearch}
                  onChange={(e) => {
                    onSetRowArticleSearch(e.target.value)
                    onSetShowRowArticleSelector(true)
                  }}
                  onFocus={() => onSetShowRowArticleSelector(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      onSetShowRowArticleSelector(false)
                      onSetRowArticleSearch('')
                    }
                  }}
                  placeholder="Lägg till artikel..."
                  className="input-field pl-8 py-1 text-sm w-full"
                />
                {showRowArticleSelector && (
                  <div className="absolute z-50 mt-1 w-full bg-[#faf9f7] dark:bg-nature-800 rounded-lg shadow-lg border border-nature-200 dark:border-nature-700 max-h-36 overflow-y-auto">
                    {articles
                      .filter(a => !row.articles.some(ra => ra.articleId === a.id))
                      .filter(a => a.name.toLowerCase().includes(rowArticleSearch.toLowerCase()))
                      .map(article => (
                        <button
                          key={article.id}
                          type="button"
                          onClick={() => onAddArticle(row.id, article.id)}
                          className="w-full px-3 py-1.5 text-left hover:bg-nature-100 dark:hover:bg-nature-700 text-sm"
                        >
                          <span className="text-nature-900 dark:text-nature-100">{article.name}</span>
                        </button>
                      ))}
                    {articles
                      .filter(a => !row.articles.some(ra => ra.articleId === a.id))
                      .filter(a => a.name.toLowerCase().includes(rowArticleSearch.toLowerCase()))
                      .length === 0 && (
                      <p className="px-3 py-1.5 text-nature-500 text-xs">Ingen matchning</p>
                    )}
                  </div>
                )}
              </div>

              {/* Articles list */}
              {row.articles.map(item => {
                const article = articles.find(a => a.id === item.articleId)
                const price = customerPrices[item.articleId] ?? 0
                return (
                  <div key={item.articleId} className="flex items-center gap-2 text-sm">
                    <span className="flex-1 text-nature-900 dark:text-nature-100">{article?.name}</span>
                    <input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) => onUpdateArticleQuantity(row.id, item.articleId, parseInt(e.target.value) || 1)}
                      className="input-field w-16 py-0.5 text-sm"
                    />
                    <span className="text-xs text-nature-500 w-8">{article?.unit || 'st'}</span>
                    {price > 0 && (
                      <span className="text-xs text-nature-500 w-20 text-right">{(price * item.quantity).toLocaleString('sv-SE')} kr</span>
                    )}
                    <button
                      onClick={() => onRemoveArticle(row.id, item.articleId)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                )
              })}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}
